import { ScheduleActivity, ScheduleCategory, WeekDay, WeeklySchedule } from '@core/models';

import {
  SaveActivityDto,
  SaveCategoryDto,
  ScheduleActivityDto,
  ScheduleCategoryDto,
  WeeklyScheduleDto,
} from './weekly-schedule.dto';

/**
 * The ONE translation between the Weekly Schedule API and the app's models.
 *
 * The two agree closely — this spec was written from the app's shape rather than
 * the other way round — so most of this is defaulting rather than renaming. That
 * defaulting is the point: the DTO types say `string` because the contract says
 * so, and a nullable column reaches the wire as null. The boundary is where that
 * gets absorbed, so past here the app's own types are honest.
 */

export function categoryFromDto(c: ScheduleCategoryDto): ScheduleCategory {
  return {
    categoryGuid: c.categoryGuid,
    label: c.label ?? '',
    colorGuid: c.colorGuid ?? '',
  };
}

/**
 * Midnight, at the two ends of one day.
 *
 * The app says an activity running to the end of the day ends at `24:00`: the
 * grid works in minutes from midnight, and 1440 is a later instant than 1320
 * while 0 is an earlier one — an end of `00:00` would sort before its own start.
 * The server says `00:00` and REFUSES `24:00`.
 *
 * Both are right for their side, so the translation lives here. Without it the
 * seeded 10 PM sleep — and anything drawn in the last row of the grid — is a 400
 * the first time it is saved.
 */
function endToDto(endTime: string): string {
  return endTime === '24:00' ? '00:00' : endTime;
}

function endFromDto(endTime: string | null | undefined): string {
  // An exclusive end of midnight can only mean the close of the day: nothing
  // crosses midnight, so no activity legitimately ends where one would start.
  return !endTime || endTime === '00:00' ? '24:00' : endTime;
}

export function activityFromDto(a: ScheduleActivityDto): ScheduleActivity {
  return {
    activityGuid: a.activityGuid,
    text: a.text ?? '',
    categoryGuid: a.categoryGuid ?? '',
    startTime: a.startTime ?? '00:00',
    endTime: endFromDto(a.endTime),
    // Field by field rather than a spread: an unknown token would otherwise
    // become a weekday the grid renders nowhere and nothing reports.
    weekdays: (a.weekdays ?? []).filter(isWeekDay),
  };
}

export function weeklyScheduleFromDto(dto: WeeklyScheduleDto): WeeklySchedule {
  return {
    personaCode: dto.personaCode,
    authored: !!dto.authored,
    categories: (dto.categories ?? []).map(categoryFromDto),
    activities: (dto.activities ?? []).map(activityFromDto),
  };
}

/** The write body for an activity — everything but the guid, which is the server's. */
export function activityToDto(a: ScheduleActivity): SaveActivityDto {
  return {
    text: a.text,
    categoryGuid: a.categoryGuid,
    startTime: a.startTime,
    endTime: endToDto(a.endTime),
    weekdays: [...a.weekdays],
  };
}

export function categoryToDto(c: ScheduleCategory): SaveCategoryDto {
  return { label: c.label, colorGuid: c.colorGuid };
}

const WEEK_DAYS = new Set<string>(['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']);

function isWeekDay(value: string): value is WeekDay {
  return WEEK_DAYS.has(value);
}
